

























































































































































import _ from 'lodash'
import Vue from 'vue'
import Component, {mixins} from 'vue-class-component'
import {Action, State} from 'vuex-class'
import Pagination, {PaginationInput} from '@/modules/core/model/pagination'
import User from '@/modules/user/model/User'
import {VDataTableOptions, VDataTablePagination} from '@/types'
import {Prop, Watch} from 'vue-property-decorator'
import moment from 'moment'
import {FindUserCriteria} from '@/modules/user/api';
import {DateMixin} from '@/modules/core/date';
import * as api from '@/modules/user/api'

const findUserCriteria = (): FindUserCriteria => ({
    excludeGroups: [],
    includeGroups: [],
    searchBy: {
        field: 'all',
        value: '',
    },
})

/**
 * Lista de usuarios
 *
 * Un completo componente que permite visualizar una tabla con la lista de usuarios
 * registrados en la aplicación.
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component({
    components: {}
})
export default class UserList extends mixins(DateMixin) {
    /**
     * Tabla seleccionable
     *
     * Al activar esta opción el usuario podrá hacer click sobre una fila de la tabla
     * para seleccionar un usuario. Al hacer click el componente emitirá un evento
     * "select" con el registro seleccionado
     *
     * @var { boolean }
     */
    @Prop({type: Boolean, default: false})
    public selectable: boolean

    /**
     * Multi-seleccionable
     */
    @Prop({type: Boolean, default: false})
    public multi: boolean

    @Prop({type: Number, default: 1})
    public page: number

    /**
     * Define la elevación del componente (Sombra)
     */
    @Prop({type: String, default: 'elevation-0'})
    public elevation: string

    /**
     * Indica si la tabla debe mostrar las acciones de edición, borrar, etc...
     */
    @Prop({type: Boolean, default: false})
    public displayActions: boolean

    @Prop({type: Boolean, default: false})
    public hideToolbar: boolean

    @Prop({type: Boolean, default: false})
    public hideToolbarButtons: boolean

    /**
     * Elimina el padding entre el borde del componente y la tabla
     */
    @Prop({type: Boolean, default: false})
    public removeTablePadding: boolean

    @Prop({type: Boolean, default: false})
    public removeTableBorder: boolean

    @Prop({type: Array, default: () => []})
    public value: User[]

    /**
     * Componente des-habilitado
     */
    @Prop({type: Boolean, default: false})
    public disabled: boolean

    /**
     * Estado aislado
     *
     * Al activar esta opción la paginación obtenida del servidor no será registrada
     * en el estado centralizado de Vuex, sobre-escribiendo la información anterior.
     *
     * En su lugar los resultados obtenidos será registrados en la propiedad local
     * del componente llamada "isolatedPagination".
     *
     * Esto es util cuando es necesario mostrar en la misma pantalla mas de un
     * componente de lista sin interferir en la información de los demás
     */
    @Prop({type: Boolean, default: false})
    public isolated: boolean

    /**
     * Ocultar botón de grupos de usuarios
     *
     * Al activar esta opción el componente dejará de mostrar el botón que permite
     * acceder a la gestión de grupos de usuarios
     */
    @Prop({type: Boolean, default: false})
    public hideUserGroupsButton: boolean

    /**
     * Paginación aislada
     *
     * Cuando el componente opera en modo aislado (activando la opción "isolated")
     * la tabla mostrará esta paginación en lugar de la lista del estado de
     * Vuex.
     */
    private isolatedPagination: Pagination<User> = {
        totalRecords: 0,
        totalPages: 0,
        items: [],
    }

    /**
     * Paginación de usuarios obtenida de la API
     */
    @State(state => state.user.usersPagination)
    public statePagination: Pagination<User>

    @Action('user/loadPagination')
    public loadPagination: (input: PaginationInput) => void

    public get pagination() {
        return this.isolated ? this.isolatedPagination : this.statePagination
    }

    /**
     * Lista personalizada de usuarios
     */
    @Prop({type: Array, default: null})
    public customItems: User[] | null

    public bottomSearch = false

    public searchByTermItems = [
        {text: 'Correo electrónico', value: 'email'},
        {text: 'Nombre completo', value: 'name'},
        {text: 'Nickname', value: 'nickname'},
    ]

    /**
     * Filtros de búsqueda
     */
    @Prop({type: Object, default: findUserCriteria})
    public filters: FindUserCriteria

    /**
     * Valor interno de los filtros de búsqueda
     */
    private internalFilters = findUserCriteria()

    public reset() {
        this.$emit('input', [])
        this.$store.commit('user/SET_USER_PAGINATION', {
            totalPages: 0,
            totalRecords: 0,
            items: [],
        })
    }

    public get isMultiSelectable() {
        return this.selectable && this.multi
    }

    public get toolbarClasses() {
        return {
            'pr-5': true,
            'pl-5': true,
            'pa-0': true,
            'border-bottom-grey': !this.$vuetify.theme.dark,
            'elevation-3': this.$vuetify.theme.dark,
            'd-none': true,
            'd-md-flex': true,
        }
    }

    /**
     * Clases CSS para la tabla de usuarios
     *
     * @var { object }
     */
    public get tableClasses() {
        return {
            'elevation-0': true,
            'border-all-grey': !this.removeTableBorder,
        }
    }

    /**
     * Cabeceras de la tabla
     */
    public get headers() {
        const headers = [
            {text: 'Id', value: 'id', sortable: false},
            {text: 'Correo', value: 'email', sortable: false},
            {text: 'Nombre', value: 'name', sortable: false},
            {text: 'Fecha de registro', value: 'registrationDate', sortable: false},
        ]

        if (this.displayActions) {
            headers.push({text: 'Acciones', value: 'id', sortable: false})
        }

        return headers
    }

    public loading = false
    public tableOptions: Partial<VDataTableOptions> = {
        sortBy: [],
        sortDesc: [],
        page: 1,
        itemsPerPage: 20,
    }

    /**
     * Observador para filtros de búsqueda
     *
     * Reacciona cuando los filtros son modificados desde el exterior del componente
     * mediante la propiedad "filters". Actualiza el valor interno de los filtros
     * y dispara una consulta de paginación
     */
    @Watch('filters', {immediate: true, deep: true})
    public async watchFilters(filters: FindUserCriteria) {
        this.internalFilters = {
            includeGroups: filters.includeGroups || [],
            excludeGroups: filters.excludeGroups || [],
            searchBy: filters.searchBy || undefined,
        }
        await this.loadTablePagination()
    }

    @Watch('tableOptions')
    public async onTablePagination(options: VDataTableOptions) {
        this.$emit('update:page', options.page)
        await this.loadTablePagination()
    }

    /**
     * Observador para la propiedad "page"
     *
     * Si la propiedad page es modificada por el padre del componente la
     * lista de usuarios lanzará una nueva paginación con el número
     * de página actualizado
     */
    @Watch('page')
    public async onPageChange(page: number) {
        this.tableOptions.page = page
        await this.loadTablePagination()
    }

    @Watch('filters.searchBy.field')
    public async onSearchByChange() {
        if (!this.userHasSearched) {
            return
        }

        await this.loadTablePagination()
    }

    public onSearch = _.debounce(async function (this: UserList, term: string) {
        // El término actual es igual al último término buscado, se detiene la carga
        // de la paginación
        const old = _.get(this.internalFilters, 'searchBy.value')
        if (term === old) {
            return
        }

        this.tableOptions.page = 1
        this.updateFilter('searchBy.value', term)
        await this.loadTablePagination()
    }, 800)

    /**
     * Actualiza un filtro de búsqueda
     */
    public updateFilter(field: string, value: any) {
        const filters = _.cloneDeep(this.filters)
        _.set(filters, field, value)
        this.internalFilters = filters
        this.$emit('update:filters', filters)

        return filters
    }

    /**
     * Indica si el usuario ha introducido algún parámetro de búsqueda
     */
    public get userHasSearched(): boolean {
        return !!(this.filters.searchBy!.value && this.filters.searchBy!.field)
    }

    /**
     * Solicita a la aplicación una paginación de departamentos en base a la paginación
     * actual de la tabla y los filtros de búsqueda
     */
    public async loadTablePagination() {
        // El componente se encuentra desactivado, no realiza peticiones para cargar
        // paginaciones durante este estado
        if (this.disabled || this.customItems) {
            return
        }

        this.loading = true
        const pagination = {
            page: this.tableOptions.page!,
            rowsPerPage: this.tableOptions.itemsPerPage!,
            sortBy: this.tableOptions.sortBy![0] || 'id',
            descending: this.tableOptions.sortDesc![0] || false,
        }

        const input: PaginationInput = {
            page: pagination.page,
            recordsPerPage: pagination.rowsPerPage,
            orderBy: {
                field: pagination.sortBy,
                direction: pagination.descending ? 'DESC' : 'ASC',
            },
            filters: this.internalFilters,
        }

        if (this.isolated) {
            this.isolatedPagination = await api.getUserPagination(input)
        } else {
            await this.loadPagination(input)
        }

        this.loading = false

        this.$emit('pagination', this.pagination)
    }

    public reload() {
        this.loadTablePagination()
    }

    public get noDataText(): string {
        return this.loading
            ? 'Cargando lista de usuarios...'
            : 'No se han encontrado resultados'
    }
}
